/**
 * Single source of truth for the tier-1 portal module navigation (PRO-0340 /
 * ADR-014 Amendment 1, D5; nested deep modules ADR-014 Amendment 3). Mirrors
 * `navConfig`: a lean, typed, `readonly` static registry — not a context-bearing
 * store (T2). The active module is **derived** from the current route via
 * `moduleForPath` (T3), never persisted (no localStorage, no PII — §10).
 *
 * **Flat modules** (Comms): tier-2 task nav in the top bar via `navConfig`.
 * **Nested modules** (HR/Finance): sub-modules expand in the side rail when
 * active; tier-2 task nav appears when a sub-module is selected (ADR-014 A3).
 */

import type { FeatureFlags, MeResponse, StaffRole } from '@ucp/web/lib';
import { hasAnyRole, navItemsForUser, type BaselineCapability } from './navConfig';
import { COMPLAINTS_ROUTE_ROLES } from './routeRoles';
import { INSIGHTS_ROUTE_ROLES } from '../insights/insightsRoles';
import { REFERRAL_ROLES } from '../referrals/referralRoles';
import { commercialEntryPath } from '../commercial/commercialRoles';
import { HR_PEOPLE_ASSISTANT_ROLES } from '../human-resources/peopleAssistantRoles';
import { HR_INTERVIEW_COMPLIANCE_ROLES } from '../human-resources/interviewComplianceRoles';
import { HR_ER_MEETING_ROLES } from '../human-resources/erMeetingRoles';
import { HR_CASE_ROLES } from '../human-resources/hrCaseRoles';
import { POLICY_ROLES } from '../policies/policyRoles';
import { ATR_ROLES, atrAdmissionIsCeoOnly } from '../hr/atrRoles';
import { FUNCTION_HEAD_ROLES } from '../../lib/atr/atrFirstGateRouting';
import { PURCHASING_BUYER_ROLES } from '../finance/purchasingRoles';
import {
  SERVICE_OPS_FINANCE_ROLES,
  SERVICE_OPS_PEOPLE_ROLES,
} from '../service-ops/serviceOpsRoles';
import { ATTENDANCE_ROUTE_ROLES } from '../attendance/attendanceRoles';
import {
  ACTIVITY_LIBRARY_GROUP_ROLES,
  TRIP_CALENDAR_READ_ROLES,
  TRIPS_ROUTE_ROLES,
} from '../trips/tripsRoles';
import { OPS_ROUTE_ROLES } from '../ops/opsRoles';
import { OFSTED_ROUTE_ROLES } from '../ofsted/ofstedRoles';
import { MARKETING_ROUTE_ROLES } from '../marketing/marketingRoles';
import { PUPIL_RISK_ROUTE_ROLES } from '../pupil-risk/pupilRiskRoles';

export type ModuleId =
  | 'comms'
  | 'complaints'
  | 'referrals'
  | 'human-resources'
  | 'finance'
  // PRO-0590 / ADR-038 — the Insights dashboards module. Flat navShape (its own
  // capability-filtered lens sub-nav lives in `InsightsModuleLayout`); registered
  // only when `UCP_INSIGHTS_ENABLED` (see `visibleModules`), never a coming-soon
  // placeholder.
  | 'insights'
  // PRO-0920 / ADR-029 A3 / ADR-014 A8 — the Commercial Data Platform (CDP) module.
  // Flat navShape (its own `/commercial/*` route tree already lives in `AppRoutes`);
  // registered only when `UCP_COMMERCIAL_DATA_ENABLED` (see `visibleModules`), never a
  // coming-soon placeholder. Member of the existing `commercial` scope group alongside
  // Referrals. Registering it retires the Comms-chrome fallback for `/commercial/*`.
  | 'commercial'
  // PRO-3050 / ADR-081 — the attendance register. Flat navShape (its own task nav lives in
  // `AttendanceSurface`); registered only when `UCP_ATTENDANCE_ENABLED` (see
  // `visibleModules`), never a coming-soon placeholder. Member of the School Ops group.
  | 'attendance'
  // PRO-0710 / ADR-083 — trips and visits. Flat navShape (its own task nav lives in
  // `TripsSurface`); registered only when `UCP_TRIPS_ENABLED` (see `visibleModules`), never a
  // coming-soon placeholder. Member of the School Ops group.
  | 'trips'
  // PRO-3100 / ADR-084 — operational issues (Weekly Report). Flat navShape (its own task nav
  // lives in `WeeklyReportSurface`); registered only when `UCP_OPS_ISSUES_ENABLED` (see
  // `visibleModules`), never a coming-soon placeholder. Member of the School Ops group.
  | 'weekly-report'
  // PRO-3140 / ADR-087 — Ofsted readiness. Flat navShape (its own task nav lives in
  // `OfstedSurface`); registered only when `UCP_OFSTED_READINESS_ENABLED` (see
  // `visibleModules`), never a coming-soon placeholder. Member of the School Ops group.
  | 'ofsted'
  // PRO-3210 / ADR-088 / ADR-014 A17 — Marketing Requests. Flat navShape (its own task nav
  // lives in `MarketingSurface`); registered only when `UCP_MARKETING_REQUESTS_ENABLED` (see
  // `visibleModules`), never a coming-soon placeholder. Standalone, after Finance in the rail.
  | 'marketing'
  // PRO-3220 Phase 3 / ADR-089 — pupil risk assessments. Flat navShape (its own task nav lives
  // in `PupilRiskSurface`); registered only when `UCP_PUPIL_RISK_ENABLED` (see
  // `visibleModules`), never a coming-soon placeholder. Member of the School Ops group.
  | 'pupil-risk';
export type ModuleStatus = 'active' | 'coming-soon';
export type NavShape = 'flat' | 'nested';

/** PO D3 (PRO-0350) — flip to launcher at this count (inclusive). */
export const MODULE_MOBILE_LAUNCHER_THRESHOLD = 5;

export type MobileModuleNavMode = 'scroll' | 'launcher';

/** Pure: selects mobile tier-1 presentation from module registry length (PRO-0350). */
export function mobileModuleNavMode(moduleCount: number): MobileModuleNavMode {
  return moduleCount >= MODULE_MOBILE_LAUNCHER_THRESHOLD ? 'launcher' : 'scroll';
}

/** Default planning disclaimer on enriched coming-soon placeholders (PRO-0480, PRD D5). */
export const PLACEHOLDER_PLANNING_DISCLAIMER =
  'Planning in progress — details may change.' as const;

export interface ModuleTask {
  readonly id: string;
  readonly label: string;
  readonly path: string;
  /** Exact-match the active route (NavLink `end`). */
  readonly end?: boolean;
  /** PRO-1440 — hide a task from callers who lack these roles (Knowledge steward-only). */
  readonly anyOf?: readonly StaffRole[];
  /**
   * PRO-1290 — a task whose own feature flag promotes it, INSIDE an already-promoted
   * sub-module. Absent on every shipped task (they are visible whenever their sub-module
   * is). Present only where one surface hosts two independently-flagged audiences, as
   * Interview Compliance now does for Employee Relations: the ER flag is a module flag in
   * its own right, so the ER tab must not appear for a recruitment-only deployment.
   * Filtered by `visibleTasks`.
   */
  readonly requiresFlag?: keyof FeatureFlags;
  /**
   * PRO-2010 (TIP-D14) — a task shown only when the matching server-computed `/me`
   * eligibility is true. `visibleTasks` hides it when the eligibility argument is absent.
   */
  readonly requiresEligibility?: keyof TaskEligibility;
}

/** PRO-2010 — `/me` eligibility booleans a task may require (see `visibleTasks`). */
export interface TaskEligibility {
  readonly hrCaseTrack: boolean;
}

/**
 * Entitlement gate for a sub-module whose route has been PROMOTED from a coming-soon
 * placeholder to a functional, role-guarded surface (the `AppRoutes` `RouteGuard`).
 * Present only on such sub-modules; a plain (still-placeholder) sub-module has no
 * gate and stays visible to everyone (visibility ≠ capability — the roadmap
 * placeholder is harmless and discoverable). See `subModuleVisible`.
 */
export interface SubModuleGate {
  /** The feature flag that promotes this sub-module from placeholder to functional. */
  readonly requiresFlag: keyof FeatureFlags;
  /**
   * Extra flags that must also be on before the surface is functional (PRO-1350
   * Epic 6 / ADR-014 A15). Absent on every pre-A15 gate.
   */
  readonly requiresAllFlags?: readonly (keyof FeatureFlags)[];
  /**
   * When true, a flag-off sub-module is omitted from nav entirely (ADR-014 A15).
   * The coming-soon route still answers a direct link. Other gated sub-modules
   * stay visible as placeholders until their flag is on.
   */
  readonly omitUntilPromoted?: boolean;
  /** The roles that confer access to the functional surface (mirrors the route guard). */
  readonly anyOf: readonly StaffRole[];
  /** PRO-1440 — admit baseline pack holders on this surface. */
  readonly baselineCapability?: BaselineCapability;
}

export interface ModuleSubModule {
  readonly id: string;
  /** Matches `apps/wiki/docs/3-modules/` EXACTLY (asserted by unit test, AC13). */
  readonly label: string;
  /** Sub-module home — equals the Home task path. */
  readonly basePath: string;
  /** One-line "what this will do" (non-PII, static). */
  readonly description: string;
  /** Staff-facing summary for placeholder routes (PRO-0480); falls back to `description`. */
  readonly placeholderSummary?: string;
  /** MkDocs slug under the wiki root, e.g. `3-modules/human-resources/complete-appraisal`. */
  readonly wikiPath?: string;
  /**
   * Entitlement gate for a promoted-to-functional sub-module. When set AND the flag
   * is on, the nav link is hidden from a caller who lacks a conferring role (so they
   * don't click through to an "access denied" dead end); when the flag is off the
   * sub-module is still a coming-soon placeholder and stays visible to everyone.
   */
  readonly gate?: SubModuleGate;
  readonly tasks: readonly ModuleTask[];
}

export interface ModuleDef {
  readonly id: ModuleId;
  /** 'Communications' | 'Complaints' | 'Referrals' | 'Human Resources' | 'Finance' — the rail/landmark accessible name. */
  readonly label: string;
  /** '/' (comms, flat for Inc 1) | '/complaints' | '/referrals' | '/hr' | '/finance'. */
  readonly basePath: string;
  readonly status: ModuleStatus;
  readonly navShape: NavShape;
  /** One-line module-overview summary for the module dashboard (coming-soon only). */
  readonly summary?: string;
  /** Staff-facing module-dashboard summary (PRO-0480); falls back to `summary`. */
  readonly placeholderSummary?: string;
  /** MkDocs slug for the module overview page. */
  readonly wikiPath?: string;
  /** Nested sub-modules (HR/Finance only; comms uses `navConfig`). */
  readonly subModules?: readonly ModuleSubModule[];
}

/**
 * Generic placeholder tasks until BDA — Complaints (ADR-014 A4). Mirrors the
 * teacher journey "record a complaint received → manage it through to
 * resolution" as two verb-noun sub-capabilities. Inert placeholders only.
 */
const RECORD_A_COMPLAINT_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Home', path: '/complaints/record-a-complaint', end: true },
  {
    id: 'new-complaint',
    label: 'New complaint',
    path: '/complaints/record-a-complaint/new-complaint',
    end: true,
  },
  {
    id: 'my-complaints',
    label: 'My complaints',
    path: '/complaints/record-a-complaint/my-complaints',
    end: true,
  },
];

const MANAGE_COMPLAINTS_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Home', path: '/complaints/manage-complaints', end: true },
  {
    id: 'in-progress',
    label: 'In progress',
    path: '/complaints/manage-complaints/in-progress',
    end: true,
  },
  { id: 'resolved', label: 'Resolved', path: '/complaints/manage-complaints/resolved', end: true },
];

/**
 * PRO-0380 functional tasks — flat Inc-1 routes when `complaintsEnabled`.
 * Empty: Inc-1 Complaints is one screen per sub-module (record form, register with
 * in-page status filter). Placeholder task slugs still redirect via `ComplaintsSurface`;
 * tier-2 task nav is omitted so status is not duplicated (Home/In progress/Resolved
 * all mapped to `/complaints`, which broke NavLink active styling).
 */
const RECORD_A_COMPLAINT_FUNCTIONAL_TASKS: readonly ModuleTask[] = [];
const MANAGE_COMPLAINTS_FUNCTIONAL_TASKS: readonly ModuleTask[] = [];

const COMPLAINT_DETAIL_PATH =
  /^\/complaints\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Generic placeholder tasks until BDA — Complete Appraisal (PO decision, ADR-014 A3). */
const SERVICE_OPS_PEOPLE_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Queue', path: '/hr/service-operations', end: true },
];

const SERVICE_OPS_FINANCE_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Queue', path: '/finance/service-operations', end: true },
];

const COMPLETE_APPRAISAL_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Home', path: '/hr/complete-appraisal', end: true },
  {
    id: 'my-appraisal',
    label: 'My appraisal',
    path: '/hr/complete-appraisal/my-appraisal',
    end: true,
  },
  { id: 'my-team', label: 'My team', path: '/hr/complete-appraisal/my-team', end: true },
];

const ATR_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Home', path: '/hr/request-new-hire-atr', end: true },
  {
    id: 'my-requests',
    label: 'My requests',
    path: '/hr/request-new-hire-atr/my-requests',
    end: true,
  },
  {
    id: 'all-requests',
    label: 'All requests',
    path: '/hr/request-new-hire-atr/all-requests',
    end: true,
    anyOf: ['COO', 'CEO', 'FINANCE_DIRECTOR', 'PEOPLE_DIRECTOR'],
    requiresFlag: 'atrEstateRegisterEnabled',
  },
  {
    id: 'pending-approval',
    label: 'Pending approval',
    path: '/hr/request-new-hire-atr/pending-approval',
    end: true,
    anyOf: [
      'OPS_DIRECTOR',
      'BUSINESS_DEVELOPMENT_DIRECTOR',
      'ESTATES_DIRECTOR',
      'FINANCE_DIRECTOR',
      'IT_DIRECTOR',
      'PEOPLE_DIRECTOR',
      'QUALITY_DIRECTOR',
    ],
  },
  /**
   * PRO-1500 Epic 3 (AC7): the Recruitment worklist. Double-gated — `requiresFlag`
   * because the step only exists on the simplified flow, and `anyOf` because AC7
   * says the task appears "only for holders of those roles". Both are needed:
   * `requiresFlag` alone would show an OD a worklist they cannot read, and `anyOf`
   * alone would advertise a route the server answers 503 on.
   */
  {
    id: 'approved-to-hire',
    label: 'Approved to hire',
    path: '/hr/request-new-hire-atr/approved-to-hire',
    end: true,
    anyOf: ['RECRUITER', 'ATR_COORDINATOR'],
    requiresFlag: 'atrSimplifiedApprovalEnabled',
  },
];

/** PRO-1100 / ADR-056/057/058 — TAG People Assistant (RAG Q&A + steward knowledge base). */
const PEOPLE_ASSISTANT_TASKS: readonly ModuleTask[] = [
  {
    id: 'home',
    label: 'Ask',
    path: '/hr/people-assistant',
    end: true,
    anyOf: HR_PEOPLE_ASSISTANT_ROLES,
  },
  {
    id: 'knowledge',
    label: 'Knowledge base',
    path: '/hr/people-assistant/knowledge',
    end: true,
    anyOf: ['HR_KNOWLEDGE_STEWARD', 'L6_PLATFORM_ADMIN'],
  },
  // PRO-2010 (PRD 54a) — never the offer flag, so pausing new raises keeps this link.
  {
    id: 'my-hr-cases',
    label: 'My HR cases',
    path: '/hr/people-assistant/cases',
    requiresFlag: 'hrCasesEnabled',
    requiresEligibility: 'hrCaseTrack',
  },
];

/** PRO-2010 / ADR-080 — the HR Cases handler queue. */
const HR_CASES_TASKS: readonly ModuleTask[] = [
  { id: 'queue', label: 'Queue', path: '/hr/cases', end: true },
];

/**
 * PRO-1120 / ADR-056 A1 / ADR-059 / ADR-060 / ADR-025 A7 — TAG People Assistant
 * Phase 2 (AI Interview Compliance Assistant). Manager runs post-interview compliance
 * analysis (Interviews); the recruitment-compliance lead governs the checklist and
 * reviews the group oversight aggregates. The steward-only Checklist/Oversight tabs
 * are shown statically but re-guarded inside the surface (server 403s regardless).
 */
const INTERVIEW_COMPLIANCE_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Interviews', path: '/hr/interview-compliance', end: true },
  { id: 'checklist', label: 'Checklist', path: '/hr/interview-compliance/checklist', end: true },
  { id: 'oversight', label: 'Oversight', path: '/hr/interview-compliance/oversight', end: true },
  // PRO-1290 / ADR-068 — Employee Relations. A nav RE-PARENT only (ADR-014): the route base
  // stays `/hr/interview-compliance`, ER hangs off it at `/er`. Carries its own
  // `requiresFlag` because `hrErMeetingsEnabled` is a module flag, not a sub-flag of
  // interview compliance — recruitment interviews and disciplinary hearings share tables
  // but not a lawful basis, an audience or a DPIA.
  {
    id: 'employee-relations',
    label: 'Employee Relations',
    path: '/hr/interview-compliance/er',
    requiresFlag: 'hrErMeetingsEnabled',
  },
];

/** PRO-1200 / ADR-062 — Policies (policy change management). Single Home tab (the
 * register); the change workspace is a per-policy nested route, not a tier-3 task. */
const POLICIES_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Register', path: '/hr/policies', end: true },
];

const CLAIM_EXPENSE_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Home', path: '/finance/claim-expense', end: true },
  { id: 'my-claims', label: 'My claims', path: '/finance/claim-expense/my-claims', end: true },
  {
    id: 'pending-approval',
    label: 'Pending approval',
    path: '/finance/claim-expense/pending-approval',
    end: true,
  },
];

const BUY_SOMETHING_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Home', path: '/finance/buy-something', end: true },
  {
    id: 'my-requests',
    label: 'My requests',
    path: '/finance/buy-something/my-requests',
    end: true,
  },
  {
    id: 'pending-approval',
    label: 'Pending approval',
    path: '/finance/buy-something/pending-approval',
    end: true,
  },
];

/** Generic placeholder tasks until BDA — Referrals (ADR-014 A6). */
const REGISTER_A_REFERRAL_TASKS: readonly ModuleTask[] = [
  // No standalone Home tab — the module-root dashboard (`/referrals`) already carries
  // the personal stat snapshot + register CTA, so the sub-module opens straight on the
  // New referral upload form (PRO-0680 follow-up); the sub-module index redirects here.
  {
    id: 'new-referral',
    label: 'New referral',
    path: '/referrals/register-a-referral/new-referral',
    end: true,
  },
  {
    id: 'my-registrations',
    label: 'My registrations',
    path: '/referrals/register-a-referral/my-registrations',
    end: true,
  },
];

const QUALIFY_REFERRALS_TASKS: readonly ModuleTask[] = [
  {
    id: 'awaiting-review',
    label: 'Awaiting review',
    path: '/referrals/qualify-referrals',
    end: true,
  },
  {
    id: 'escalated',
    label: 'Escalated',
    path: '/referrals/qualify-referrals/escalated',
    end: true,
  },
  {
    id: 'returned',
    label: 'Returned',
    path: '/referrals/qualify-referrals/returned',
    end: true,
  },
];

// PRO-0430 EP6 — Oversight (BD-Director). Capability-gated at render/route
// (`referral_oversight_read`; L6 excluded); the tab appears for every referral
// role but the dashboard/data 403s server-side for a caller without the capability.
const REFERRAL_OVERSIGHT_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Home', path: '/referrals/oversight', end: true },
];

// PRO-0430 Epic E — My Coverage (self-serve LA-coverage editor). Single-screen tab;
// the editor + its data calls are routing-flag + `referral_review` gated server-side
// (503/403 without the flag/capability), mirroring the Oversight tab's server-gated
// visibility pattern.
const MY_COVERAGE_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Home', path: '/referrals/my-coverage', end: true },
];

// PRO-0430 Epic F — Service Profiles (suitability scoring calibration, US F.1/F.2).
// Single-screen tab; the editor + its config data calls are calibration-SUB-flag +
// `referral_oversight_read` gated server-side (503/403 without the flag/capability),
// mirroring the Oversight tab's server-gated visibility pattern.
const SERVICE_PROFILES_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Home', path: '/referrals/service-profiles', end: true },
];

const MANAGE_REFERRAL_QUEUE_TASKS: readonly ModuleTask[] = [
  { id: 'home', label: 'Home', path: '/referrals/manage-referral-queue', end: true },
  {
    id: 'in-progress',
    label: 'In progress',
    path: '/referrals/manage-referral-queue/in-progress',
    end: true,
  },
  {
    id: 'at-risk',
    label: 'At risk',
    path: '/referrals/manage-referral-queue/at-risk',
    end: true,
  },
];

export const modulesConfig: readonly ModuleDef[] = [
  { id: 'comms', label: 'Communications', basePath: '/', status: 'active', navShape: 'flat' },
  // PRO-0590 / ADR-038 — Insights dashboards. Flat module (own lens sub-nav);
  // functional when `UCP_INSIGHTS_ENABLED`, hidden entirely otherwise (filtered by
  // `visibleModules`, never a coming-soon placeholder). Lenses are capability-gated
  // in `AppRoutes` (Enterprise: CEO/COO/OPS_DIRECTOR; School: PRINCIPAL/OPS_DIRECTOR;
  // Student: inert placeholder for any Insights viewer).
  {
    id: 'insights',
    label: 'Insights',
    basePath: '/insights',
    status: 'active',
    navShape: 'flat',
    summary: 'Governed, PII-safe insight dashboards across Enterprise, School and Student lenses.',
    wikiPath: '3-modules/unified-comms/insights',
  },
  // PRO-0920 / ADR-029 A3 — the Commercial Data Platform (CDP) module. Flat + active
  // (its `/commercial/*` authoring surfaces already exist in `AppRoutes`, capability-
  // gated); hidden from tier-1 nav until `UCP_COMMERCIAL_DATA_ENABLED` (see
  // `visibleModules`), never a coming-soon placeholder — mirrors `insights`. No
  // `wikiPath`: that metadata is consumed only for coming-soon placeholder rendering,
  // so an active flat module omits it (test-safe — the wiki-path assertions cover
  // coming-soon modules only).
  {
    id: 'commercial',
    label: 'Commercial',
    basePath: '/commercial',
    status: 'active',
    navShape: 'flat',
    summary:
      'Commercial Data Platform — customers, product catalogue and pricing, with contracts, placements and billing to follow.',
  },
  // PRO-3050 / ADR-081 — the attendance register. Flat + active; hidden from tier-1 nav until
  // `UCP_ATTENDANCE_ENABLED` (see `visibleModules`), mirroring `insights`. No `wikiPath`:
  // that metadata is read only for coming-soon placeholders.
  {
    id: 'attendance',
    label: 'Attendance',
    basePath: '/attendance',
    status: 'active',
    navShape: 'flat',
    summary: 'Take the attendance register, follow up today’s registers and report attendance.',
  },
  // PRO-0710 / ADR-083 — trips and visits. Flat + active; hidden from tier-1 nav until
  // `UCP_TRIPS_ENABLED` (see `visibleModules`), mirroring `attendance`.
  {
    id: 'trips',
    label: 'Trips',
    basePath: '/trips',
    status: 'active',
    navShape: 'flat',
    summary: 'Raise a trip or visit, route it for approval and keep its documents together.',
  },
  // PRO-3100 / ADR-084 — operational issues. Flat + active; hidden from tier-1 nav until
  // `UCP_OPS_ISSUES_ENABLED` (see `visibleModules`), mirroring `trips`.
  {
    id: 'weekly-report',
    label: 'Weekly Report',
    basePath: '/weekly-report',
    status: 'active',
    navShape: 'flat',
    summary: 'Record operational issues, give out actions, escalate and confirm resolution.',
  },
  // PRO-3140 / ADR-087 — Ofsted readiness. Flat + active; hidden from tier-1 nav until
  // `UCP_OFSTED_READINESS_ENABLED` (see `visibleModules`), mirroring `weekly-report`.
  {
    id: 'ofsted',
    label: 'Ofsted',
    basePath: '/ofsted',
    status: 'active',
    navShape: 'flat',
    summary: 'Self-assess Ofsted readiness per provision and regime, and track the gaps.',
  },
  // PRO-3210 / ADR-088 — Marketing Requests. Flat + active; hidden from tier-1 nav until
  // `UCP_MARKETING_REQUESTS_ENABLED` (see `visibleModules`), mirroring `ofsted`.
  {
    id: 'marketing',
    label: 'Marketing',
    basePath: '/marketing',
    status: 'active',
    navShape: 'flat',
    summary: 'Ask Marketing and Communications for help, and track your requests.',
  },
  // PRO-3220 Phase 3 / ADR-089 — pupil risk assessments. Flat + active; hidden from tier-1 nav
  // until `UCP_PUPIL_RISK_ENABLED` (see `visibleModules`), mirroring `trips`.
  {
    id: 'pupil-risk',
    label: 'Pupil risk',
    basePath: '/pupil-risk',
    status: 'active',
    navShape: 'flat',
    summary: 'Write, approve and review each pupil’s risk assessment, and keep the register.',
  },
  {
    id: 'complaints',
    label: 'Complaints',
    basePath: '/complaints',
    status: 'coming-soon',
    navShape: 'nested',
    summary:
      'Complaints handling — record a complaint received and manage it through to resolution — coming soon.',
    wikiPath: '3-modules/complaints',
    placeholderSummary:
      'Record and manage complaints on the shared portal — from logging a new complaint through triage and action to resolution. The goal is a consistent, inspection-grade register for schools and Central Services oversight.',
    subModules: [
      {
        id: 'record-a-complaint',
        label: 'Record a Complaint',
        basePath: '/complaints/record-a-complaint',
        description: 'Log a new complaint received and capture the details.',
        wikiPath: '3-modules/complaints/record-a-complaint',
        placeholderSummary:
          'Log a new complaint received — in person, by phone, email, or letter — with enough structured detail to triage and act. This is the front door of the complaints workflow for school staff.',
        tasks: RECORD_A_COMPLAINT_TASKS,
      },
      {
        id: 'manage-complaints',
        label: 'Manage Complaints',
        basePath: '/complaints/manage-complaints',
        description: 'Track complaints through triage and action to resolution.',
        wikiPath: '3-modules/complaints/manage-complaints',
        placeholderSummary:
          'Track complaints through triage, action, and resolution with status visibility for handlers and oversight roles. Supports consistent management from first record through to closure.',
        tasks: MANAGE_COMPLAINTS_TASKS,
      },
    ],
  },
  {
    id: 'referrals',
    label: 'Referrals',
    basePath: '/referrals',
    status: 'coming-soon',
    navShape: 'nested',
    summary:
      'Referral Intelligence Platform (RIP) — AI-assisted EHCP and placement referral intake, extraction, summarisation and scoring, with human review and qualification and Zoho CRM sync — coming soon.',
    wikiPath: '3-modules/referrals',
    placeholderSummary:
      'Referral Intelligence Platform (RIP) — AI-assisted intake of EHCP and placement referral packs with OCR, extraction, summarisation and scoring, always followed by mandatory human review. Approved referrals sync to Zoho CRM; AI accelerates review but never decides placement.',
    subModules: [
      {
        id: 'register-a-referral',
        label: 'Register a Referral',
        basePath: '/referrals/register-a-referral',
        description:
          'Upload or ingest an EHCP/placement referral pack; the immutable original is stored and AI extraction runs.',
        wikiPath: '3-modules/referrals/register-a-referral',
        placeholderSummary:
          'Upload or ingest an incoming EHCP or placement referral pack and capture intake metadata. The immutable original is stored and the pack enters the AI pipeline and shared triage queue for human review.',
        tasks: REGISTER_A_REFERRAL_TASKS,
      },
      {
        id: 'qualify-referrals',
        label: 'Qualify Referrals',
        basePath: '/referrals/qualify-referrals',
        description:
          'Review AI extraction, summary and scores against the source document; edit, override and record the human decision.',
        wikiPath: '3-modules/referrals/qualify-referrals',
        placeholderSummary:
          'Review AI extraction, summary, and suitability scores against the source document; edit, override, and record the human qualification decision. No referral progresses without explicit human approval.',
        tasks: QUALIFY_REFERRALS_TASKS,
      },
      {
        id: 'manage-referral-queue',
        label: 'Manage Referral Queue',
        basePath: '/referrals/manage-referral-queue',
        description: 'Prioritise and track referrals through the admissions pipeline.',
        wikiPath: '3-modules/referrals/manage-referral-queue',
        placeholderSummary:
          'Prioritise and track referrals through the admissions pipeline with SLA visibility for Business Development and admissions roles. Supports responsive handling of Local Authority and commissioner referrals.',
        tasks: MANAGE_REFERRAL_QUEUE_TASKS,
      },
      {
        id: 'my-coverage',
        label: 'My Coverage',
        basePath: '/referrals/my-coverage',
        description:
          'Manage the local authorities you cover and your primary (single-point-of-contact) LAs; primary coverage auto-assigns incoming referrals to you.',
        wikiPath: '3-modules/referrals/my-coverage',
        placeholderSummary:
          'Set the local authorities you cover as a business-development owner, and mark the ones for which you are the single point of contact. Referrals arriving from a primary LA are auto-assigned to you.',
        tasks: MY_COVERAGE_TASKS,
      },
      {
        id: 'oversight',
        label: 'Oversight',
        basePath: '/referrals/oversight',
        description:
          'PII-free oversight aggregates (status, ageing, SLA-risk, reviewer workload) and the tamper-evident inspection/SAR export, for the Admissions Director.',
        wikiPath: '3-modules/referrals/oversight',
        placeholderSummary:
          'Group-level, PII-free oversight of the whole referral queue — status and priority mix, ageing and SLA-risk, and reviewer workload. Also produces the attested, tamper-evident inspection and SAR export pack for a named child.',
        tasks: REFERRAL_OVERSIGHT_TASKS,
      },
      {
        id: 'service-profiles',
        label: 'Service Profiles',
        basePath: '/referrals/service-profiles',
        description:
          'Maintain the Aurora service profiles (needs, region, age range, residential offer, capacity) and per-service factor weights that calibrate the advisory suitability score.',
        wikiPath: '3-modules/referrals/service-profiles',
        placeholderSummary:
          'Maintain the catalogue of Aurora services and their capacity/factor tuning that the AI suitability score is calibrated against. Coded reference data only — the score stays advisory and every referral decision remains with a human.',
        tasks: SERVICE_PROFILES_TASKS,
      },
    ],
  },
  {
    id: 'human-resources',
    label: 'Human Resources',
    basePath: '/hr',
    status: 'coming-soon',
    navShape: 'nested',
    summary:
      'People and HR workflows — appraisals, hiring authority, and interviews — coming soon.',
    wikiPath: '3-modules/human-resources',
    placeholderSummary:
      'People and HR workflows for school and service leaders — performance appraisals, authority to recruit, and interviewing — delivered inside TAG Operations. Structured intake and approval will replace informal spreadsheets and email chains while iTrent remains the HR system of record.',
    subModules: [
      {
        id: 'people-assistant',
        label: 'Ask TAG',
        basePath: '/hr/people-assistant',
        description: 'Ask grounded HR-policy questions; steward the approved knowledge base.',
        wikiPath: '3-modules/human-resources/people-assistant-project-brief',
        placeholderSummary:
          'Ask questions about Aurora HR policy and general employment guidance and get answers drawn only from approved sources, with citations and recommended next steps. A central HR knowledge steward governs the approved source set; answers are guidance, not legal advice or a decision.',
        gate: {
          requiresFlag: 'hrAssistantEnabled',
          anyOf: [...HR_PEOPLE_ASSISTANT_ROLES, 'L6_PLATFORM_ADMIN'],
          // Disjunctive with `anyOf` (`hasAnyRole`): baseline `hr_assistant_ask` still
          // admits Ask (AC18). L6 matches `anyOf` and does not need the pack.
          baselineCapability: 'hr_assistant_ask',
        },
        tasks: PEOPLE_ASSISTANT_TASKS,
      },
      {
        id: 'interview-compliance',
        label: 'Interview Compliance',
        basePath: '/hr/interview-compliance',
        description:
          'AI-assisted post-interview compliance analysis; every output manager-reviewed.',
        wikiPath: '3-modules/human-resources/people-assistant-project-brief',
        placeholderSummary:
          'Run an AI-assisted compliance review of a completed interview: mandatory safeguarding questions, employment gaps, DBS and Right-to-Work discussion, competencies evidenced, question consistency, and potentially inappropriate questioning — each anchored to the transcript. A manager reviews every output; the assistant never makes or shapes a hiring decision.',
        gate: {
          requiresFlag: 'hrInterviewComplianceEnabled',
          // PRO-1290 — the UNION with the ER roles, mirroring the `AppRoutes` guard on the
          // shared surface. Without it an HR_CASE_MANAGER (who holds no recruitment role)
          // would have the sub-module link hidden the moment the recruitment flag came on,
          // even though the Employee Relations task inside it is theirs.
          anyOf: [...HR_INTERVIEW_COMPLIANCE_ROLES, ...HR_ER_MEETING_ROLES],
        },
        tasks: INTERVIEW_COMPLIANCE_TASKS,
      },
      {
        // PRO-1200 / ADR-062 — Policies (policy change management). First owned by HR
        // (org-wide standalone module later). Register a policy, propose/approve/publish a
        // change (the governed Aurora→SharePoint write-back), and re-ground the People
        // Assistant knowledge base. Flag- + role-gated functional surface.
        id: 'policies',
        label: 'Policies',
        basePath: '/hr/policies',
        description: 'Register, review, approve and publish org-wide policy changes.',
        wikiPath: '3-modules/human-resources',
        placeholderSummary:
          'Register org-wide policies and take a policy change through a governed lifecycle — propose, review, approve, and publish. Publishing writes the approved document back to the SharePoint Policies library and re-grounds the Ask TAG knowledge base. The change history is an inspection-grade, append-only audit trail.',
        gate: {
          requiresFlag: 'policyManagementEnabled',
          anyOf: POLICY_ROLES,
          baselineCapability: 'policy_read',
        },
        tasks: POLICIES_TASKS,
      },
      {
        id: 'request-new-hire-atr',
        label: 'Request New Hire (ATR)',
        basePath: '/hr/request-new-hire-atr',
        description: 'Approval gate before recruitment starts; output is authority to hire.',
        wikiPath: '3-modules/human-resources/atr-project-brief',
        placeholderSummary:
          'Request authority to recruit for a new or replacement role — commonly called an ATR at Aurora. A structured workflow will replace informal spreadsheet and email chains, routing through delegated approval before interviewing begins.',
        gate: { requiresFlag: 'atrEnabled', anyOf: ATR_ROLES },
        tasks: ATR_TASKS,
      },
      {
        id: 'complete-appraisal',
        label: 'Complete Appraisal',
        basePath: '/hr/complete-appraisal',
        description: 'Performance cycles, goals, and manager sign-off.',
        wikiPath: '3-modules/human-resources/complete-appraisal',
        placeholderSummary:
          'Run appraisal and performance cycles for school and service staff: goals, review meetings, and manager sign-off. Today this work is often fragmented across spreadsheets, email, and iTrent — this capability will orchestrate the cycle with an audit trail.',
        tasks: COMPLETE_APPRAISAL_TASKS,
      },
      {
        id: 'service-operations',
        label: 'Service Operations',
        basePath: '/hr/service-operations',
        description: 'Review and resolve the People shared mailbox queue.',
        wikiPath: '3-modules/human-resources/service-operations',
        placeholderSummary:
          'A workbench for the corporate People shared mailbox: review classified requests, age the queue, and approve, amend, reject or escalate a draft reply. Safeguarding and other mandatory-human mail stay on this queue.',
        gate: {
          requiresFlag: 'serviceOpsPeopleEnabled',
          requiresAllFlags: ['serviceOpsEnabled'],
          omitUntilPromoted: true,
          anyOf: SERVICE_OPS_PEOPLE_ROLES,
        },
        tasks: SERVICE_OPS_PEOPLE_TASKS,
      },
      {
        id: 'hr-cases',
        label: 'HR Cases',
        basePath: '/hr/cases',
        description: 'Work the HR cases colleagues escalate from Ask TAG.',
        wikiPath: '3-modules/human-resources/runbook/hr-cases-triage',
        placeholderSummary:
          'A queue for the HR team to take, reply to and resolve the questions colleagues escalate from Ask TAG. Restricted cases, internal notes and legal holds are handled here with an append-only audit trail.',
        gate: { requiresFlag: 'hrCasesEnabled', anyOf: HR_CASE_ROLES },
        tasks: HR_CASES_TASKS,
      },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    basePath: '/finance',
    status: 'coming-soon',
    navShape: 'nested',
    summary: 'Transactional finance workflows — expenses and purchasing — coming soon.',
    wikiPath: '3-modules/finance',
    placeholderSummary:
      'Transactional finance workflows for staff — expense claims and purchasing — on the shared portal. The aim is to make the compliant route the easiest route, with Unit4 as the finance system of record.',
    subModules: [
      {
        id: 'claim-expense',
        label: 'Claim Expense',
        basePath: '/finance/claim-expense',
        description: 'Submit and approve staff expense claims.',
        wikiPath: '3-modules/finance/claim-expense',
        placeholderSummary:
          'Submit, approve, and track staff expense claims with receipt capture and policy checks. Approved claims hand off to Unit4 for reimbursement processing.',
        tasks: CLAIM_EXPENSE_TASKS,
      },
      {
        id: 'buy-something',
        label: 'Buy something',
        basePath: '/finance/buy-something',
        description: 'Request a purchase through approval to a purchase order (PO).',
        wikiPath: '3-modules/finance/buy-something',
        placeholderSummary:
          'Request purchase of goods and services through a guided, consumer-grade experience while the platform resolves supplier, budget, approver, and purchase order creation in Unit4. The compliant process should become the easiest process — reducing maverick spend outside governed workflows.',
        gate: { requiresFlag: 'purchasingAmazonEnabled', anyOf: PURCHASING_BUYER_ROLES },
        tasks: BUY_SOMETHING_TASKS,
      },
      {
        id: 'service-operations',
        label: 'Service Operations',
        basePath: '/finance/service-operations',
        description: 'Review and resolve the Finance shared mailbox queue.',
        wikiPath: '3-modules/finance/service-operations',
        placeholderSummary:
          'A workbench for the corporate Finance shared mailbox: review classified requests, age the queue, and approve, amend, reject or escalate a draft reply. This is the operator queue, not the expense or purchasing intake forms.',
        gate: {
          requiresFlag: 'serviceOpsFinanceEnabled',
          requiresAllFlags: ['serviceOpsEnabled'],
          omitUntilPromoted: true,
          anyOf: SERVICE_OPS_FINANCE_ROLES,
        },
        tasks: SERVICE_OPS_FINANCE_TASKS,
      },
    ],
  },
];

/**
 * Pure: returns the module whose surface owns this path. Comms keeps flat Inc-1
 * paths, so anything not under `/hr` or `/finance` is Comms (the default
 * fallback) — including unknown routes, so the rail never shows a broken active
 * state on a 404 (Debt PRO-0340-D1).
 */
export function moduleForPath(pathname: string): ModuleId {
  if (pathname === '/complaints' || pathname.startsWith('/complaints/')) {
    return 'complaints';
  }
  if (pathname === '/referrals' || pathname.startsWith('/referrals/')) {
    return 'referrals';
  }
  if (pathname === '/hr' || pathname.startsWith('/hr/')) {
    return 'human-resources';
  }
  if (pathname === '/finance' || pathname.startsWith('/finance/')) {
    return 'finance';
  }
  // PRO-0590 — Insights owns `/insights[/*]` (only reachable when the flag mounts it).
  if (pathname === '/insights' || pathname.startsWith('/insights/')) {
    return 'insights';
  }
  // PRO-0920 — Commercial owns `/commercial[/*]` (CDP surfaces, only reachable when
  // `UCP_COMMERCIAL_DATA_ENABLED` mounts them). Additive branch placed before the
  // `comms` fallback so `/commercial/*` no longer renders under Comms chrome; every
  // other branch above is byte-for-byte unchanged (AC-2 / PRO-0600 AC-3). Exact
  // `=== '/commercial'` + `startsWith('/commercial/')` matching keeps `/commercialz`
  // falling back to `comms`.
  if (pathname === '/commercial' || pathname.startsWith('/commercial/')) {
    return 'commercial';
  }
  // PRO-3050 — Attendance owns `/attendance[/*]` (only reachable when the flag mounts it).
  if (pathname === '/attendance' || pathname.startsWith('/attendance/')) {
    return 'attendance';
  }
  // PRO-0710 — Trips owns `/trips[/*]` (only reachable when the flag mounts it).
  if (pathname === '/trips' || pathname.startsWith('/trips/')) {
    return 'trips';
  }
  // PRO-3100 — Weekly Report owns `/weekly-report[/*]` (only reachable when the flag mounts it).
  if (pathname === '/weekly-report' || pathname.startsWith('/weekly-report/')) {
    return 'weekly-report';
  }
  // PRO-3140 — Ofsted owns `/ofsted[/*]` (only reachable when the flag mounts it).
  if (pathname === '/ofsted' || pathname.startsWith('/ofsted/')) {
    return 'ofsted';
  }
  // PRO-3210 — Marketing owns `/marketing[/*]` (only reachable when the flag mounts it).
  if (pathname === '/marketing' || pathname.startsWith('/marketing/')) {
    return 'marketing';
  }
  // PRO-3220 Phase 3 — Pupil risk owns `/pupil-risk[/*]` (only reachable when the flag mounts it).
  if (pathname === '/pupil-risk' || pathname.startsWith('/pupil-risk/')) {
    return 'pupil-risk';
  }
  return 'comms';
}

/**
 * PRO-0590 / ADR-038 — the modules visible in tier-1 nav for a given flag state.
 * The Insights module is registered in `modulesConfig` but hidden entirely (not a
 * coming-soon placeholder) until `UCP_INSIGHTS_ENABLED`; every other module is
 * always visible (their own flags flip active↔coming-soon, not visible↔hidden).
 * Applied at the three render sites (`ModuleRail`, `ModuleNav`, `ModuleLauncher`)
 * so the flag is threaded once, not through every nav component.
 */
export function visibleModules(
  insightsEnabled = false,
  commercialDataEnabled = false,
  attendanceEnabled = false,
  tripsEnabled = false,
  opsIssuesEnabled = false,
  ofstedEnabled = false,
  marketingEnabled = false,
  pupilRiskEnabled = false,
): readonly ModuleDef[] {
  // PRO-0920 — the `commercial` module is hidden from tier-1 nav until
  // `UCP_COMMERCIAL_DATA_ENABLED` (never a coming-soon placeholder — mirrors the
  // Insights `insightsEnabled` filter). `commercialDataEnabled` is the SECOND
  // positional arg (defaulting false) so the shipped single-arg call sites
  // (`visibleModules(insightsEnabled)`) keep today's behaviour — backward-compatible.
  return modulesConfig.filter((module) => {
    if (module.id === 'insights') {
      return insightsEnabled;
    }
    if (module.id === 'commercial') {
      return commercialDataEnabled;
    }
    // PRO-3050 — third positional arg, default false, so existing calls are unchanged.
    if (module.id === 'attendance') {
      return attendanceEnabled;
    }
    // PRO-0710 — fourth positional arg, default false, so existing calls are unchanged.
    if (module.id === 'trips') {
      return tripsEnabled;
    }
    // PRO-3100 — fifth positional arg, default false, so existing calls are unchanged.
    if (module.id === 'weekly-report') {
      return opsIssuesEnabled;
    }
    // PRO-3140 — sixth positional arg, default false, so existing calls are unchanged.
    // PRO-3150 — callers pass `ofstedModuleEnabled(flags)`: readiness or inspection.
    if (module.id === 'ofsted') {
      return ofstedEnabled;
    }
    // PRO-3210 — seventh positional arg, default false, so existing calls are unchanged.
    if (module.id === 'marketing') {
      return marketingEnabled;
    }
    // PRO-3220 Phase 3 — eighth positional arg, default false, so existing calls are unchanged.
    if (module.id === 'pupil-risk') {
      return pupilRiskEnabled;
    }
    return true;
  });
}

/**
 * Should a sub-module's nav link be shown to this caller? A GATED sub-module (one
 * promoted to a functional, role-guarded route) is hidden when the flag is ON
 * and the caller holds no conferring role — so an unentitled user no longer clicks a
 * menu item that silently bounces them (previously to Comms Home). When the flag is
 * OFF the sub-module is still a coming-soon placeholder and stays visible (visibility
 * ≠ capability), unless `omitUntilPromoted` is set (Service Operations: the menu
 * entry exists only while the module and domain flags are on). An ungated
 * sub-module (plain placeholder) is always visible. The server (route guard +
 * capability + RLS) remains authoritative — this is nav UX only.
 */
function gateIsPromoted(gate: SubModuleGate, flags: Partial<FeatureFlags>): boolean {
  if (flags[gate.requiresFlag] !== true) {
    return false;
  }
  return (gate.requiresAllFlags ?? []).every((flag) => flags[flag] === true);
}

export function subModuleVisible(
  sub: ModuleSubModule,
  user: Pick<MeResponse, 'roles'> & Partial<Pick<MeResponse, 'workforceBaseline'>>,
  flags: Partial<FeatureFlags>,
): boolean {
  const gate = sub.gate;
  if (!gate) {
    return true;
  }
  if (!gateIsPromoted(gate, flags)) {
    return gate.omitUntilPromoted !== true;
  }
  if (!hasAnyRole(user, gate.anyOf, undefined, gate.baselineCapability)) {
    return false;
  }
  // PRO-3190. A CEO-only caller sees the functional ATR module only while All
  // requests is on. Other ATR roles are unchanged. The placeholder (flag off)
  // stays on the existing path above.
  if (
    sub.id === 'request-new-hire-atr' &&
    atrAdmissionIsCeoOnly(user.roles) &&
    flags.atrEstateRegisterEnabled !== true
  ) {
    return false;
  }
  return true;
}

/**
 * PRO-1290 — the tasks of one sub-module that this flag state should show. Only a task
 * carrying `requiresFlag` can be hidden; every shipped task has none and is returned
 * unchanged, so this is a no-op for every pre-1290 sub-module. Pure, so the nav renderer
 * and its test share one definition of "which tabs exist right now".
 *
 * PRO-2010 — `eligibility` carries server-computed `/me` booleans; a task with
 * `requiresEligibility` fails closed when it is omitted.
 */
export function visibleTasks(
  sub: ModuleSubModule,
  flags: Partial<FeatureFlags>,
  user?: Pick<MeResponse, 'roles'> & Partial<Pick<MeResponse, 'workforceBaseline'>>,
  eligibility?: TaskEligibility,
): readonly ModuleTask[] {
  const needsFilter = sub.tasks.some(
    (task) =>
      task.requiresFlag !== undefined ||
      task.anyOf !== undefined ||
      task.requiresEligibility !== undefined,
  );
  if (!needsFilter) {
    return sub.tasks;
  }
  return sub.tasks.filter((task) => {
    if (task.requiresFlag !== undefined && flags[task.requiresFlag] !== true) {
      return false;
    }
    if (
      task.requiresEligibility !== undefined &&
      eligibility?.[task.requiresEligibility] !== true
    ) {
      return false;
    }
    // PRO-1500 Epic 3 (peer review SF-004): fail CLOSED when no caller is supplied.
    // Previously a missing `user` skipped this filter and SHOWED a role-gated task,
    // so "only holders of these roles see it" held because both shell call sites
    // happen to pass `user` — not by construction. `user` is optional on
    // `SubModuleTaskNav`, so a future renderer could have opened the gate silently.
    if (task.anyOf && (!user || !hasAnyRole(user, task.anyOf))) {
      return false;
    }
    // PRO-3190. My requests stays for every existing ATR role. A CEO-only caller
    // has Home and All requests, and no list of their own.
    if (
      task.id === 'my-requests' &&
      task.path === '/hr/request-new-hire-atr/my-requests' &&
      user &&
      atrAdmissionIsCeoOnly(user.roles)
    ) {
      return false;
    }
    // PRO-1700: Directors only see Pending approval when the Function Head
    // flag is on. Dual-hat OD + Director still sees the OD list when it is off.
    if (
      task.id === 'pending-approval' &&
      task.path === '/hr/request-new-hire-atr/pending-approval' &&
      flags.atrFunctionHeadEnabled !== true &&
      user &&
      !user.roles.some((assignment) => assignment.role === 'OPS_DIRECTOR') &&
      user.roles.some((assignment) => FUNCTION_HEAD_ROLES.includes(assignment.role))
    ) {
      return false;
    }
    return true;
  });
}

/**
 * Returns the tier-1 module set with each module's `subModules` filtered to those the
 * caller may see (`subModuleVisible`). Applied ONCE in the shell (mirrors
 * `visibleModules`), so every downstream nav renderer — the desktop rail
 * (`railModel` sections + flat nesting via `subModulesForModule`), the mobile drawer
 * (`ModuleLauncher`), and the sub-module tab strip (`ModuleSubModuleNav`) — operates
 * on the already-entitled set without threading roles/flags through each component.
 * Route generation (`nestedPlaceholderPaths`) and active-state detection
 * (`subModuleForPath`) read the canonical `modulesConfig`, so they are unaffected.
 */
export function entitledModules(
  modules: readonly ModuleDef[],
  user: Pick<MeResponse, 'roles'>,
  flags: Partial<FeatureFlags>,
): readonly ModuleDef[] {
  return modules.map((module) => {
    if (!module.subModules) {
      return module;
    }
    const filtered = module.subModules.filter((sub) => subModuleVisible(sub, user, flags));
    if (filtered.length === module.subModules.length) {
      return module;
    }
    return { ...module, subModules: filtered };
  });
}

/**
 * The role + flag test that governs a module's own destinations — the module-level
 * mirror of the `AppRoutes` RouteGuard (PRO-1360 / ADR-014 A14).
 *
 * Each entry names the SAME constant its route guard spreads and the SAME flag that
 * mounts the route, so chrome and guard cannot drift into disagreement. Only modules
 * that own a guarded route tree appear here: Comms is derived (its `/` route is
 * deliberately ungated) and HR/Finance are derived from their children, since neither
 * has a module-level route to guard.
 */
interface ModuleChromeGate {
  readonly requiresFlag: keyof FeatureFlags;
  /** PRO-3150 — the module is on when `requiresFlag` or any of these is (Ofsted, TIP-D18). */
  readonly orFlags?: readonly (keyof FeatureFlags)[];
  readonly anyOf: readonly StaffRole[];
  /** PRO-1440 — admit baseline pack holders on this module chrome surface. */
  readonly baselineCapability?: BaselineCapability;
  /** PRO-0710 Increment 1.2 TD-10 — extra roles admitted only while their flag is on. */
  readonly flagRoles?: readonly {
    readonly flag: keyof FeatureFlags;
    readonly anyOf: readonly StaffRole[];
  }[];
}

export const MODULE_CHROME_GATES: Partial<Record<ModuleId, ModuleChromeGate>> = {
  complaints: { requiresFlag: 'complaintsEnabled', anyOf: COMPLAINTS_ROUTE_ROLES },
  referrals: { requiresFlag: 'referralsEnabled', anyOf: REFERRAL_ROLES },
  insights: { requiresFlag: 'insightsEnabled', anyOf: INSIGHTS_ROUTE_ROLES },
  // PRO-3070 — Commercial is not a single role list. `moduleIsChrome` calls
  // `commercialEntryPath` so a data-platform reader and a placement caller get
  // different link targets, and a referrals-only caller is not admitted.
  attendance: { requiresFlag: 'attendanceEnabled', anyOf: ATTENDANCE_ROUTE_ROLES },
  trips: {
    requiresFlag: 'tripsEnabled',
    anyOf: TRIPS_ROUTE_ROLES,
    flagRoles: [
      { flag: 'tripsSchoolCalendarReadEnabled', anyOf: TRIP_CALENDAR_READ_ROLES },
      { flag: 'tripsActivityLibraryEnabled', anyOf: ACTIVITY_LIBRARY_GROUP_ROLES },
    ],
  },
  'weekly-report': { requiresFlag: 'opsIssuesEnabled', anyOf: OPS_ROUTE_ROLES },
  ofsted: {
    requiresFlag: 'ofstedReadinessEnabled',
    orFlags: ['ofstedInspectionEnabled'],
    anyOf: OFSTED_ROUTE_ROLES,
  },
  // PRO-3210 — every admitted colleague holds `marketing_request_submit` by the baseline pack.
  marketing: {
    requiresFlag: 'marketingRequestsEnabled',
    anyOf: MARKETING_ROUTE_ROLES,
    baselineCapability: 'marketing_request_submit',
  },
  'pupil-risk': { requiresFlag: 'pupilRiskEnabled', anyOf: PUPIL_RISK_ROUTE_ROLES },
};

/**
 * Is this sub-module a real, enterable destination for this caller right now?
 *
 * Gated → the flag must be on AND the caller must hold a conferring role. A gated
 * sub-module whose flag is off is still a coming-soon placeholder, which is exactly
 * what entitlement-first chrome omits — note this inverts `subModuleVisible`, which
 * returns true in that state to advertise the placeholder, so it is only reachable
 * behind the kill-switch.
 *
 * Ungated → functional only while the parent module is EFFECTIVELY active. "No gate
 * means placeholder" would be wrong: Complaints' `record-a-complaint` and
 * `manage-complaints` carry no gate yet are real surfaces once `complaintsEnabled`.
 * HR and Finance never promote, so their ungated children stay placeholders.
 */
type ChromeUser = Pick<MeResponse, 'roles'> & Partial<Pick<MeResponse, 'workforceBaseline'>>;

export function subModuleIsChrome(
  module: ModuleDef,
  sub: ModuleSubModule,
  user: ChromeUser,
  flags: Partial<FeatureFlags>,
): boolean {
  const gate = sub.gate;
  if (gate) {
    return (
      gateIsPromoted(gate, flags) &&
      hasAnyRole(user, gate.anyOf, undefined, gate.baselineCapability)
    );
  }
  return (
    effectiveModuleStatus(module, flags.complaintsEnabled, flags.referralsEnabled) === 'active'
  );
}

/**
 * Is this module itself a chrome destination for this caller?
 *
 * @param otherEntitledIds ids already admitted on this pass. Consulted only for Comms.
 */
export function moduleIsChrome(
  module: ModuleDef,
  user: ChromeUser,
  flags: Partial<FeatureFlags>,
  chromeSubModules: readonly ModuleSubModule[],
  otherEntitledIds: ReadonlySet<ModuleId>,
): boolean {
  if (module.id === 'comms') {
    // Comms' own `/` route is deliberately unguarded, so the route table cannot supply
    // the test — derive it from the nav items Comms owns via `moduleForPath`, which
    // stops four `/commercial/*` items keeping Communications alive for a steward.
    //
    // Comms is ALSO the host of last resort: `PrimaryNav` renders only while Comms is
    // the active module, so it is those mis-homed items' only render site. Without the
    // second clause a Principal on a contracts-on/data-off deployment owns no Comms
    // item, Commercial is hidden by `visibleModules`, and `/commercial/placements`
    // has nowhere left to appear.
    return navItemsForUser(user, flags).some((item) => {
      const owner = moduleForPath(item.path);
      return owner === 'comms' || !otherEntitledIds.has(owner);
    });
  }
  // PRO-3070 — data readers and placement callers are admitted; referrals-only are not.
  // Contracts-on/data-off still uses the Comms host of last resort, because this
  // branch also requires `commercialDataEnabled` (the module is not in the rail).
  if (module.id === 'commercial') {
    return flags.commercialDataEnabled === true && commercialEntryPath(user, flags) !== null;
  }
  const gate = MODULE_CHROME_GATES[module.id];
  if (gate) {
    const anyOf = [
      ...gate.anyOf,
      ...(gate.flagRoles ?? []).flatMap((extra) => (flags[extra.flag] === true ? extra.anyOf : [])),
    ];
    return (
      [gate.requiresFlag, ...(gate.orFlags ?? [])].some((flag) => flags[flag] === true) &&
      hasAnyRole(user, anyOf, undefined, gate.baselineCapability)
    );
  }
  // A nested module with no module-level route (HR, Finance) is reachable exactly as
  // far as its children are. This is also what collapses an emptied rail section.
  return chromeSubModules.length > 0;
}

/**
 * The entitlement-first chrome set (PRO-1360 / ADR-014 A14) — applied ONCE in
 * `RoutedAppShell` so every downstream renderer stays dumb.
 *
 * Two passes, because Comms' host-of-last-resort clause depends on which other modules
 * survived. When the kill-switch is off this delegates to the shipped `entitledModules`
 * unchanged, which is what makes the existing shell suites the flag-off contract.
 */
export function entitledChrome(
  modules: readonly ModuleDef[],
  user: ChromeUser,
  flags: Partial<FeatureFlags>,
): readonly ModuleDef[] {
  if (flags.navEntitlementFilterEnabled !== true) {
    return entitledModules(modules, user, flags);
  }

  const withChildren = modules.map((module) => ({
    module,
    subModules: module.subModules?.filter((sub) => subModuleIsChrome(module, sub, user, flags)),
  }));

  const admittedIds = new Set<ModuleId>();
  for (const { module, subModules } of withChildren) {
    if (module.id === 'comms') {
      continue;
    }
    if (moduleIsChrome(module, user, flags, subModules ?? [], admittedIds)) {
      admittedIds.add(module.id);
    }
  }

  const result: ModuleDef[] = [];
  for (const { module, subModules } of withChildren) {
    const admitted =
      module.id === 'comms'
        ? moduleIsChrome(module, user, flags, subModules ?? [], admittedIds)
        : admittedIds.has(module.id);
    if (!admitted) {
      continue;
    }
    const narrowed = subModules !== undefined && subModules.length !== module.subModules?.length;
    let next: ModuleDef = narrowed ? { ...module, subModules } : module;
    // Link target only. `moduleForPath` still uses the registry `/commercial` prefix.
    if (module.id === 'commercial') {
      const entry = commercialEntryPath(user, flags);
      if (entry && entry !== next.basePath) {
        next = { ...next, basePath: entry };
      }
    }
    result.push(next);
  }
  return result;
}

/** True when the path is the module dashboard (`/hr`, `/finance`) — no tier-2 task nav. */
export function isModuleDashboard(pathname: string, complaintsEnabled = false): boolean {
  const moduleId = moduleForPath(pathname);
  if (moduleId === 'comms') {
    return false;
  }
  const module = moduleById(moduleId);
  // Functional Complaints (PRO-0380) has no separate dashboard — `/complaints` is the register.
  if (moduleId === 'complaints' && complaintsEnabled) {
    return false;
  }
  return module.navShape === 'nested' && pathname === module.basePath;
}

/**
 * Effective tier-1 status for nav chrome (mobile launcher badge, etc.).
 * Static `ModuleDef.status` stays `coming-soon` for kill-switch rollback; when a
 * feature flag promotes the module, chrome must not show "Coming soon".
 */
export function effectiveModuleStatus(
  module: ModuleDef,
  complaintsEnabled = false,
  referralsEnabled = false,
): ModuleStatus {
  if (module.id === 'complaints' && complaintsEnabled) {
    return 'active';
  }
  // PRO-0430 EP1 — Referrals promotes to 'active' behind `UCP_REFERRALS_ENABLED`
  // (mirrors the Complaints promotion). `referralsEnabled` is the THIRD positional
  // arg, so the shipped `effectiveModuleStatus(referrals, true)` call (arg 2 =
  // complaintsEnabled) stays 'coming-soon' — backward-compatible.
  if (module.id === 'referrals' && referralsEnabled) {
    return 'active';
  }
  return module.status;
}

/** Sub-modules for rail / task nav — swaps Complaints to functional paths when enabled. */
export function subModulesForModule(
  module: ModuleDef,
  complaintsEnabled = false,
  profileSettingsEnabled = false,
): readonly ModuleSubModule[] {
  if (module.id === 'complaints' && complaintsEnabled) {
    // Pass the module we were GIVEN, not the registry — it may already have been
    // narrowed to the caller's entitled children (PRO-1360).
    return complaintsFunctionalSubModules(module);
  }
  const subs = module.subModules ?? [];
  // PRO-0740 / ADR-014 amendment (SF-2): when the Profile Settings page is on, the
  // Referrals "My coverage" and "Service profiles" surfaces are re-homed to the
  // account drawer (`/account`) and their old routes redirect there, so drop them
  // from the Referrals tier-2 strip (6 -> 4 tabs). `profileSettingsEnabled` is the
  // THIRD positional arg, so shipped two-arg calls keep today's 6 tabs — backward-
  // compatible (mirrors how `complaintsEnabled` / `referralsEnabled` are threaded).
  if (module.id === 'referrals' && profileSettingsEnabled) {
    return subs.filter((sub) => sub.id !== 'my-coverage' && sub.id !== 'service-profiles');
  }
  return subs;
}

/**
 * A leading, module-root tab for the tier-2 sub-module strip. Distinct from a
 * `ModuleSubModule` (which is a wiki-taxonomy capability with its own tier-3 tasks):
 * this is the module's landing dashboard at `module.basePath`, surfaced as the first
 * tab so it is reachable without returning to the tier-1 rail (the "click Referrals
 * in the side menu just to see the dashboard" gap). It is NOT in `subModules`, so it
 * never participates in `subModuleForPath` / placeholder-route generation.
 */
export interface ModuleRootTab {
  readonly label: string;
  /** The module-root path this tab links to and is active on (exact match only). */
  readonly to: string;
  /** Key into `SUB_MODULE_ICONS` (`navIcons.ts`) — nav-icon registry, no inline lucide. */
  readonly iconId: string;
}

/**
 * The module-root "Dashboard" tab for a nested module whose root path renders a
 * landing dashboard, or `null` for every other module (the render stays generic).
 *
 * Referrals is the only such module today: the role-adaptive home dashboard at
 * `/referrals` (ADR-038 A3, PRO-0680) is otherwise only reachable by clicking the
 * tier-1 "Referrals" rail link. Gated on `referralsDashboardEnabled` because when the
 * flag is off `/referrals` redirects to Register-a-Referral (a tab would dead-loop);
 * the server (`requireReferralsDashboardEnabled`) stays authoritative for the data.
 */
export function moduleRootTab(
  module: ModuleDef,
  referralsDashboardEnabled = false,
): ModuleRootTab | null {
  if (module.id === 'referrals' && referralsDashboardEnabled) {
    return { label: 'Dashboard', to: module.basePath, iconId: 'dashboard' };
  }
  return null;
}

/**
 * PRO-0600 follow-up (refines ADR-014 A3): should this nested module surface its
 * sub-modules as a **page-top tab strip** (the ATR task-bar pattern) instead of
 * nesting them in the left rail? True in two cases:
 *
 * 1. **Terminal sub-modules** — every sub-module owns a single screen with no tier-3
 *    tasks (functional Complaints: Record a Complaint · Manage Complaints). A rail
 *    nest would be a childless expansion, so page-tab regardless of the rail flag.
 * 2. **Grouped nested member** — in the scope-grouped rail (`navRestructureEnabled`)
 *    a nested module that belongs to a group (Complaints in School Ops, Referrals in
 *    Commercial) would otherwise sit at rail depth 3 (group → module → sub-modules);
 *    surface its sub-modules as page tabs to keep the rail shallow. When such a
 *    module's sub-modules own tasks (Referrals), the active sub-module's task strip
 *    still renders below the sub-module strip (the mobile layout, all viewports).
 *
 * Standalone **sections** (HR / Finance) are rail depth-1 headers, so they keep their
 * sub-modules nested in the rail (ADR-014 A9); the flat rail (flag off) keeps nesting
 * for non-terminal modules too.
 */
export function subModulesAsPageTabs(
  module: ModuleDef,
  complaintsEnabled = false,
  navRestructureEnabled = false,
  profileSettingsEnabled = false,
): boolean {
  if (module.navShape !== 'nested') {
    return false;
  }
  const subs = subModulesForModule(module, complaintsEnabled, profileSettingsEnabled);
  if (subs.length === 0) {
    return false;
  }
  if (subs.every((sub) => sub.tasks.length === 0)) {
    return true;
  }
  return navRestructureEnabled && groupForModule(module.id) !== null;
}

/**
 * PRO-0380 functional sub-capabilities (flat routes). Labels/descriptions stay
 * aligned with the ADR-014 A4 placeholder registry; only paths differ.
 */
export function complaintsFunctionalSubModules(
  complaints: ModuleDef = moduleById('complaints'),
): readonly ModuleSubModule[] {
  // PRO-1360 — takes the module it should read rather than always re-reading the
  // canonical registry. The shell filters `subModules` once and threads the result
  // down; re-reading here silently restored the unfiltered pair, so a caller with no
  // Complaints entitlement still got both tabs whenever `complaintsEnabled` was on.
  // The default argument keeps every route-ownership caller (which MUST see the
  // canonical pair regardless of chrome) on today's behaviour.
  //
  // The non-null assertions this replaced are gone deliberately: once the shell can
  // filter a child out, `find` may legitimately return undefined and asserting would
  // throw rather than render a narrowed strip.
  const subs = complaints.subModules ?? [];
  const record = subs.find((sub) => sub.id === 'record-a-complaint');
  const manage = subs.find((sub) => sub.id === 'manage-complaints');
  const functional: ModuleSubModule[] = [];
  if (record) {
    functional.push({
      ...record,
      basePath: '/complaints/new',
      tasks: RECORD_A_COMPLAINT_FUNCTIONAL_TASKS,
    });
  }
  if (manage) {
    functional.push({
      ...manage,
      basePath: '/complaints',
      tasks: MANAGE_COMPLAINTS_FUNCTIONAL_TASKS,
    });
  }
  return functional;
}

function complaintsFunctionalSubModuleForPath(pathname: string): ModuleSubModule | undefined {
  const subs = complaintsFunctionalSubModules();
  if (pathname === '/complaints/new') {
    return subs.find((sub) => sub.id === 'record-a-complaint');
  }
  if (pathname.startsWith('/complaints/oversight')) {
    return undefined;
  }
  if (
    pathname === '/complaints' ||
    COMPLAINT_DETAIL_PATH.test(pathname) ||
    pathname.startsWith('/complaints/manage-complaints')
  ) {
    return subs.find((sub) => sub.id === 'manage-complaints');
  }
  if (pathname.startsWith('/complaints/record-a-complaint')) {
    return subs.find((sub) => sub.id === 'record-a-complaint');
  }
  return undefined;
}

/** Whether `sub` owns the current path (for rail active styling). */
export function subModuleOwnsPath(
  sub: ModuleSubModule,
  pathname: string,
  moduleId: ModuleId,
  complaintsEnabled = false,
): boolean {
  if (moduleId === 'complaints' && complaintsEnabled) {
    return complaintsFunctionalSubModuleForPath(pathname)?.id === sub.id;
  }
  return pathname === sub.basePath || pathname.startsWith(`${sub.basePath}/`);
}

/** The nested sub-module owning this path, if any. */
export function subModuleForPath(
  pathname: string,
  complaintsEnabled = false,
): ModuleSubModule | undefined {
  const moduleId = moduleForPath(pathname);
  if (moduleId === 'comms') {
    return undefined;
  }
  const module = moduleById(moduleId);
  if (module.navShape !== 'nested') {
    return undefined;
  }
  if (moduleId === 'complaints' && complaintsEnabled) {
    return complaintsFunctionalSubModuleForPath(pathname);
  }
  return module.subModules?.find(
    (sub) => pathname === sub.basePath || pathname.startsWith(`${sub.basePath}/`),
  );
}

/** The task item for a nested-module path, if registered. */
export function taskForPath(pathname: string, complaintsEnabled = false): ModuleTask | undefined {
  const sub = subModuleForPath(pathname, complaintsEnabled);
  if (!sub) {
    return undefined;
  }
  return sub.tasks.find((task) => pathname === task.path);
}

/** All placeholder task paths for nested coming-soon modules (route table generation). */
export interface NestedPlaceholderEntry {
  readonly path: string;
  readonly title: string;
  readonly description: string;
  readonly placeholderSummary: string;
  readonly wikiPath?: string;
  readonly planningNote: string;
}

export function nestedPlaceholderPaths(): readonly NestedPlaceholderEntry[] {
  const entries: NestedPlaceholderEntry[] = [];
  for (const module of modulesConfig) {
    if (module.navShape !== 'nested') {
      continue;
    }
    for (const sub of module.subModules ?? []) {
      const placeholderSummary = sub.placeholderSummary ?? sub.description;
      for (const task of sub.tasks) {
        entries.push({
          path: task.path,
          title: task.id === 'home' ? sub.label : task.label,
          description: sub.description,
          placeholderSummary,
          ...(sub.wikiPath !== undefined ? { wikiPath: sub.wikiPath } : {}),
          planningNote: PLACEHOLDER_PLANNING_DISCLAIMER,
        });
      }
    }
  }
  return entries;
}

/** The `ModuleDef` for an id (never undefined — every `ModuleId` is registered). */
export function moduleById(id: ModuleId): ModuleDef {
  const found = modulesConfig.find((module) => module.id === id);
  if (!found) {
    throw new Error(`Unknown module id: ${id}`);
  }
  return found;
}

/**
 * Portal-ops surface class (PRO-0440 / ADR-014 Amendment 5). A third surface class
 * alongside flat/nested modules: the bottom-aligned, L6-only "Settings" cluster that
 * hosts platform administration at `/platform/*`. Kept as a parallel `readonly`
 * registry + pure selector (mirrors `modulesConfig` / `moduleForPath`) rather than
 * overloading `ModuleId`, so the rail/shell logic stays pure and unit-testable.
 */
export type PortalOpsId = 'platform';

export interface PortalOpsDef {
  readonly id: PortalOpsId;
  readonly label: string;
  readonly basePath: string;
  readonly defaultPath: string;
  readonly anyOf: readonly StaffRole[];
}

export const portalOpsConfig: readonly PortalOpsDef[] = [
  {
    id: 'platform',
    label: 'Settings',
    basePath: '/platform',
    defaultPath: '/platform/schools',
    anyOf: ['L6_PLATFORM_ADMIN'],
  },
];

/**
 * Pure: returns the portal-ops surface owning this path, or `null`. The shell
 * consults this FIRST so `/platform/*` never renders Comms chrome even though
 * `moduleForPath` still falls back to `'comms'` for unknown paths.
 */
export function portalOpsForPath(pathname: string): PortalOpsId | null {
  if (pathname === '/platform' || pathname.startsWith('/platform/')) {
    return 'platform';
  }
  return null;
}

/**
 * Scope/function grouping construct (PRO-0600 / ADR-014 Amendment 8, SF-01). A
 * PARALLEL `readonly` registry + pure selector — mirrors the `portalOpsConfig` /
 * `portalOpsForPath` precedent above — deliberately NOT an `ModuleId` overload and
 * NOT a URL re-root: a group is a module's tier-1 NAV PARENT only. Comms/Complaints/
 * Referrals keep their existing routes (`moduleForPath` is untouched, AC-3); the
 * group merely decides how the tier-1 rail is sectioned when
 * `UCP_NAV_RESTRUCTURE_ENABLED` is on. Groups are labelled, NON-interactive rail
 * sections (member modules are the interactive links), so interactive nav depth
 * stays 2 (module → task) and the two-`nav`-landmark contract holds (MF-02 / TD-3).
 */
export type GroupId = 'school-ops' | 'commercial';

export interface GroupDef {
  readonly id: GroupId;
  /** Rail/landmark section label — 'School Ops' | 'Commercial' (AC-5). */
  readonly label: string;
  /** One-line section summary (CF-01 — avoids an empty-container read). */
  readonly summary: string;
  /** Member modules, in rail order. */
  readonly memberModuleIds: readonly ModuleId[];
}

export const groupsConfig: readonly GroupDef[] = [
  {
    id: 'school-ops',
    label: 'School Ops',
    summary:
      'School-facing operations — parent communications, complaints handling, attendance, trips and visits, pupil risk assessments, the weekly operational report, and Ofsted readiness.',
    memberModuleIds: [
      'comms',
      'complaints',
      'attendance',
      'trips',
      'pupil-risk',
      'weekly-report',
      'ofsted',
    ],
  },
  {
    id: 'commercial',
    label: 'Commercial',
    // PRO-0920 — the CDP `commercial` module joins Referrals as a co-member of the
    // existing A8 scope group (the group itself shipped in PRO-0600). Referrals stays.
    summary:
      'Commercial operations — referral intake, the Commercial Data Platform, and (soon) contracts, placements and billing.',
    memberModuleIds: ['referrals', 'commercial'],
  },
];

/**
 * Pure: the group owning a module, or `null` for a standalone module (Insights,
 * Human Resources, Finance). Composed as `groupForModule(moduleForPath(path))` for
 * group active-state. Derived from `groupsConfig` so a membership change is a
 * one-line registry edit (no selector churn).
 */
export function groupForModule(moduleId: ModuleId): GroupId | null {
  const group = groupsConfig.find((candidate) => candidate.memberModuleIds.includes(moduleId));
  return group ? group.id : null;
}

/** The `GroupDef` for an id (never undefined — every `GroupId` is registered). */
export function groupById(id: GroupId): GroupDef {
  const found = groupsConfig.find((group) => group.id === id);
  if (!found) {
    throw new Error(`Unknown group id: ${id}`);
  }
  return found;
}

/**
 * A single ordered tier-1 rail entry. A discriminated union so the rail render is
 * exhaustive and type-safe (no `any`):
 * - `group`   — a labelled, NON-interactive scope header (School Ops / Commercial)
 *               with its present member modules as the interactive links.
 * - `section` — a standalone NESTED module (Human Resources / Finance) rendered in
 *               the SAME labelled-header shape as a group: a non-interactive header
 *               whose sub-modules are the always-visible interactive links (PRO-0600
 *               follow-up — "settle on the School Ops pattern for all"). The module
 *               dashboard (`/hr`, `/finance`) is intentionally NOT a nav target; you
 *               go straight to a sub-task.
 * - `module`  — a standalone FLAT module (Insights) rendered as a single link.
 */
export type RailEntry =
  | { readonly kind: 'group'; readonly group: GroupDef; readonly members: readonly ModuleDef[] }
  | {
      readonly kind: 'section';
      readonly module: ModuleDef;
      readonly subModules: readonly ModuleSubModule[];
    }
  | { readonly kind: 'module'; readonly module: ModuleDef };

/**
 * The flag-on tier-1 order (ADR-014 A8, Insights first — CF-02/TD-6):
 * Insights · School Ops[Communications, Complaints] · Human Resources · Finance ·
 * Commercial[Referrals]. A group token expands to its `memberModuleIds` filtered to
 * those actually present in the supplied (already `visibleModules`-filtered) set, so
 * e.g. an insights-off registry simply omits the Insights entry (flag matrix
 * `(on, off)`), and a group with no present members is dropped entirely.
 */
type RailOrderToken =
  | { readonly kind: 'module'; readonly moduleId: ModuleId }
  | { readonly kind: 'group'; readonly groupId: GroupId };

const GROUPED_RAIL_ORDER: readonly RailOrderToken[] = [
  { kind: 'module', moduleId: 'insights' },
  { kind: 'group', groupId: 'school-ops' },
  { kind: 'module', moduleId: 'human-resources' },
  { kind: 'module', moduleId: 'finance' },
  // PRO-3210 / ADR-014 A17 — standalone, after Finance.
  { kind: 'module', moduleId: 'marketing' },
  { kind: 'group', groupId: 'commercial' },
];

/**
 * Ordered tier-1 rail entries (PRO-0600). Flag on → the ADR-014 A8 grouped order
 * above, where a standalone nested module (HR/Finance) becomes a `section` (the
 * scope-header shape, PRO-0600 follow-up) and a standalone flat module (Insights)
 * stays a `module`. Flag off → each module wrapped as `{ kind: 'module', module }`
 * in the UNCHANGED `visibleModules`/`modulesConfig` order, emitting NO group/section
 * entries (SF-002 byte-for-byte / AC-7). `modules` is the already flag-filtered set from
 * `visibleModules(insightsEnabled)`, so the caller controls Insights visibility;
 * `railModel` never re-adds a hidden module.
 */
export function railModel(
  modules: readonly ModuleDef[],
  navRestructureEnabled: boolean,
): readonly RailEntry[] {
  if (!navRestructureEnabled) {
    return modules.map((module) => ({ kind: 'module', module }));
  }
  const byId = new Map<ModuleId, ModuleDef>(modules.map((module) => [module.id, module]));
  const entries: RailEntry[] = [];
  for (const token of GROUPED_RAIL_ORDER) {
    if (token.kind === 'module') {
      const module = byId.get(token.moduleId);
      if (module) {
        // A standalone NESTED module (Human Resources / Finance) adopts the
        // scope-header shape: a non-interactive header with its sub-modules as the
        // always-visible links — uniform with the group sections. A standalone FLAT
        // module (Insights) stays a single link. Only HR/Finance carry sub-modules
        // here, so `module.subModules` needs no complaints-swap (that lives inside a
        // group, rendered as a member module).
        const subModules = module.subModules ?? [];
        if (module.navShape === 'nested' && subModules.length > 0) {
          entries.push({ kind: 'section', module, subModules });
        } else {
          entries.push({ kind: 'module', module });
        }
      }
      continue;
    }
    const group = groupById(token.groupId);
    const members = group.memberModuleIds
      .map((memberId) => byId.get(memberId))
      .filter((module): module is ModuleDef => module !== undefined);
    if (members.length > 0) {
      entries.push({ kind: 'group', group, members });
    }
  }
  return entries;
}
